/* Gavequiz: gift-finder quiz (sections/gift-quiz.liquid) and its result bar on the
   collection page (snippets/gift-quiz-result-bar.liquid). Self-contained: no cart, no
   theme JS dependencies. */
(function () {
  'use strict';

  const STORAGE_KEY = 'giftQuizResult';
  const FETCH_TIMEOUT = 4000;
  const ANSWER_KEYS = ['qg', 'qt', 'qs', 'qr', 'qm'];
  const STEP_KEYS = { gender: 'qg', type: 'qt', watch_type: 'qs', jewelry: 'qs', ring_size: 'qr', metal: 'qm' };
  const TRACK_NAMES = { gender: 'recipient', type: 'gift_type', watch_type: 'watch_type', jewelry: 'jewelry_type', ring_size: 'ring_size', metal: 'metal' };

  // Result bar fallbacks, used when the visitor didn't arrive from the quiz in this tab.
  const RESULT_DEFAULTS = {
    quizUrl: '/pages/gavequiz',
    pronouns: {
      hende: { pronoun_subject: 'hun', pronoun_object: 'hende' },
      ham: { pronoun_subject: 'han', pronoun_object: 'ham' }
    },
    labels: {
      qs: { klassisk: 'Klassiske ure', kronograf: 'Kronografer', mekanisk: 'Mekaniske ure', digital: 'Digitalure', halskaede: 'Halskæder', armbaand: 'Armbånd', oreringe: 'Øreringe', ring: 'Ringe' },
      qt: { ur: 'Ure', smykke: 'Smykker' },
      qm: { guld: 'guld', forgyldt: 'forgyldt sølv', soelv: 'sølv', staal: 'stål' }
    },
    budgetMap: {
      param_min: 'filter.v.price.gte',
      param_max: 'filter.v.price.lte',
      brackets: {
        'under-500': { label: 'Under 500 kr.', max: 499 },
        '500-1000': { label: '500-1.000 kr.', min: 500, max: 1000 },
        '1000-2500': { label: '1.000-2.500 kr.', min: 1001, max: 2500 },
        '2500-5000': { label: '2.500-5.000 kr.', min: 2501, max: 5000 },
        'over-5000': { label: 'Over 5.000 kr.', min: 5001 }
      }
    },
    result: {
      noticeMetal: 'Vi fandt ingen {type} i {metal}. Her er de nærmeste i andre farver.',
      noticeMix: 'Vi fandt ingen præcise match. Her er vores mest populære gaver til {pronoun_object}.',
      retake: 'Tag quizzen igen',
      chipsHeading: 'Indsnævr resultatet',
      chipActive: 'valgt',
      chipsPrice: 'Pris',
      chipsWatch: 'custom.watches_rem_material, custom.watches_diameter',
      chipsJewelry: 'custom.stone'
    }
  };

  /* ---------- helpers ---------- */

  const isDebug = () => /[?&]quizdebug=1(&|$)/.test(window.location.search) || Boolean(window.Shopify && window.Shopify.designMode);
  const warn = (...args) => { if (isDebug()) console.warn('[Gavequiz]', ...args); };
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const fill = (template, vars) => String(template || '').replace(/\{(\w+)\}/g, (match, key) => (vars[key] !== undefined ? vars[key] : match));
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const collectionPath = (handle) => `${root()}collections/${encodeURIComponent(handle)}`;

  function track(event, data) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: event }, data || {}));
  }

  function parseJSON(value, fallback, label) {
    if (value && typeof value === 'object') return value;
    try {
      return JSON.parse(value);
    } catch (e) {
      console.error(`[Gavequiz] Invalid JSON in "${label}" setting`, e);
      return fallback;
    }
  }

  function fetchWithTimeout(url) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT);
    return fetch(url, { signal: controller.signal, credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status} for ${url}`);
        return response.text();
      })
      .finally(() => clearTimeout(timer));
  }

  const fetchJSON = (url) => fetchWithTimeout(url).then((text) => JSON.parse(text));

  // params: array of [name, value] pairs. Repeated names = OR within a filter.
  function buildQuery(params) {
    const search = new URLSearchParams();
    params.forEach(([name, value]) => search.append(name, value));
    return search.toString();
  }

  // Cached per collection: the availability checks and the final lookup share one request
  const filterCache = new Map();
  function getFilters(handle) {
    if (!filterCache.has(handle)) {
      filterCache.set(handle, fetchJSON(`${collectionPath(handle)}?view=quiz-filters`).catch((error) => {
        filterCache.delete(handle);
        throw error;
      }));
    }
    return filterCache.get(handle);
  }

  const withTimeout = (promise, ms) => Promise.race([promise, wait(ms).then(() => null)]);

  function targetHandle(state, config) {
    const map = config.collectionMap;
    const key = state.qt === 'mix' ? `${state.qg}|mix` : `${state.qg}|${state.qt}|${state.qs}`;
    return map[key] || map[`${state.qg}|mix`];
  }

  function getCount(handle, params) {
    const query = buildQuery(params.concat([['view', 'quiz-count']]));
    return fetchJSON(`${collectionPath(handle)}?${query}`).then((data) => Number(data.count) || 0);
  }

  /* ---------- routing ---------- */

  // Metal filter pairs for a gift type, keeping only param names and values that exist on the collection.
  function metalParams(filters, metalMap, type, metal) {
    if (!metal || metal === 'vedikke' || type === 'mix') return [];
    const mapping = metalMap[type];
    if (!mapping || !mapping.values || !mapping.values[metal]) {
      warn(`No metal mapping for "${type}" / "${metal}"`);
      return [];
    }
    const filter = filters.filters.find((f) => f.param_name === mapping.param);
    if (!filter) {
      warn(`Filter "${mapping.param}" does not exist on "${filters.handle}" - metal filter skipped`);
      return [];
    }
    return mapping.values[metal].filter((value) => {
      const exists = filter.values.includes(value);
      if (!exists) warn(`Value "${value}" does not exist in "${mapping.param}" on "${filters.handle}" - skipped`);
      return exists;
    }).map((value) => [mapping.param, value]);
  }

  // Price params for a bracket (result bar "Pris" chips), without checking the collection
  function bracketParams(budgetMap, bracket) {
    const minParam = budgetMap.param_min || 'filter.v.price.gte';
    const maxParam = budgetMap.param_max || 'filter.v.price.lte';
    const params = [];
    if (bracket.min !== undefined && bracket.min !== null && Number(bracket.min) > 0) params.push([minParam, String(Math.round(bracket.min))]);
    if (bracket.max !== undefined && bracket.max !== null) params.push([maxParam, String(Math.round(bracket.max))]);
    return params;
  }

  async function resolveResult(state, config) {
    const map = config.collectionMap;
    const mixHandle = map[`${state.qg}|mix`];
    const key = state.qt === 'mix' ? `${state.qg}|mix` : `${state.qg}|${state.qt}|${state.qs}`;
    const handle = targetHandle(state, config);
    if (!map[key]) warn(`No collection mapped for "${key}" - using "${mixHandle}"`);

    const result = (target, params, fallback) => ({ handle: target, params: params, fallback: fallback || '' });

    try {
      const filters = await getFilters(handle);
      const metal = metalParams(filters, config.metalMap, state.qt, state.qm);
      const metalWanted = state.qt !== 'mix' && state.qm && state.qm !== 'vedikke';

      // A metal answer with no usable filter values can't be honoured: treat it as the metal fallback
      if ((!metalWanted || metal.length) && await getCount(handle, metal) > 0) return result(handle, metal);

      // 1. Drop the metal filter
      if (metalWanted && await getCount(handle, []) > 0) return result(handle, [], 'metal');

      // 2. The collection is empty: the recipient's mix collection
      if (mixHandle && mixHandle !== handle) return result(mixHandle, [], 'mix');
      return result(handle, []);
    } catch (error) {
      // Timeout or error: go to the unfiltered target
      warn('Lookup failed, redirecting to the unfiltered collection', error);
      return result(handle, []);
    }
  }

  function resultUrl(target, state) {
    const params = target.params.slice();
    params.push(['quiz', '1']);
    ANSWER_KEYS.forEach((key) => {
      if (state[key] && key !== 'qr') params.push([key, state[key]]);
    });
    if (target.fallback) params.push(['qf', target.fallback]);
    return `${collectionPath(target.handle)}?${buildQuery(params)}`;
  }

  /* ---------- quiz ---------- */

  class GiftQuiz extends HTMLElement {
    connectedCallback() {
      const raw = parseJSON(this.querySelector('[data-gift-quiz-config]').textContent, {}, 'config');
      this.config = Object.assign({}, raw, {
        collectionMap: parseJSON(raw.collectionMap, {}, 'Kollektioner'),
        metalMap: parseJSON(raw.metalMap, {}, 'Metal-filtre'),
        budgetMap: parseJSON(raw.budgetMap, { brackets: {} }, 'Budget-intervaller')
      });

      this.steps = {};
      this.querySelectorAll('[data-step]').forEach((el) => { this.steps[el.dataset.step] = el; });
      this.chrome = this.querySelector('[data-quiz-chrome]');
      this.progressBar = this.querySelector('[data-quiz-progress]');
      this.live = this.querySelector('[data-quiz-live]');
      this.state = {};
      this.current = null;
      this.firstStep = this.steps.start ? 'start' : 'gender';
      this.metalSkipped = new Set();
      this.availabilityCache = new Map();

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      this.onPopState = () => this.renderFromHash();
      this.onPageShow = (event) => { if (event.persisted) this.renderFromHash(); };
      window.addEventListener('popstate', this.onPopState);
      window.addEventListener('pageshow', this.onPageShow);

      if (window.Shopify && window.Shopify.designMode) {
        this.onBlockSelect = (event) => this.previewBlock(event);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      if (!history.state || !history.state.giftQuiz) {
        history.replaceState({ giftQuiz: true, depth: 0 }, '', window.location.href);
      }
      this.renderFromHash(false);
      if (isDebug()) this.runDebug();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      window.removeEventListener('pageshow', this.onPageShow);
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    /* state & navigation */

    path(state = this.state) {
      const steps = ['gender', 'type'];
      if (state.qt === 'smykke') {
        steps.push('jewelry');
        if (state.qs === 'ring') steps.push('ring_size');
        steps.push('metal');
      } else if (state.qt !== 'mix') {
        steps.push('watch_type', 'metal');
      }
      // Metal step skipped when the collection has at most one usable metal (see checkAvailability)
      if (steps.includes('metal') && this.metalSkipped && this.metalSkipped.has(targetHandle(state, this.config))) {
        steps.splice(steps.indexOf('metal'), 1);
      }
      return steps;
    }

    nextStep(step) {
      if (step === 'ring_size' && this.state.qr === 'nej') return 'ring_note';
      if (step === 'ring_note') return 'metal';
      const steps = this.path();
      const index = steps.indexOf(step);
      return index < steps.length - 1 ? steps[index + 1] : 'submit';
    }

    previousStep(step) {
      if (step === 'ring_note') return 'ring_size';
      const steps = this.path();
      const index = steps.indexOf(step);
      return index > 0 ? steps[index - 1] : this.firstStep;
    }

    hashFor(step) {
      if (step === this.firstStep) return '';
      const params = new URLSearchParams({ step: step });
      ANSWER_KEYS.forEach((key) => { if (this.state[key]) params.set(key, this.state[key]); });
      return `#${params.toString()}`;
    }

    navigate(step, { replace = false } = {}) {
      const depth = (history.state && history.state.depth) || 0;
      const url = `${window.location.pathname}${window.location.search}${this.hashFor(step)}`;
      if (replace) {
        history.replaceState({ giftQuiz: true, depth: depth }, '', url);
      } else {
        history.pushState({ giftQuiz: true, depth: depth + 1 }, '', url);
      }
      this.show(step);
    }

    // Restore from the URL hash; falls back to the first unanswered step.
    renderFromHash(focus = true) {
      const params = new URLSearchParams(window.location.hash.slice(1));
      const previous = this.state || {};
      this.state = {};
      ANSWER_KEYS.forEach((key) => { if (params.get(key)) this.state[key] = params.get(key); });
      // Going back: keep later answers (shown as selected) while the earlier ones are unchanged
      for (const key of ANSWER_KEYS) {
        if (this.state[key] && this.state[key] !== previous[key]) break;
        if (!this.state[key] && previous[key]) this.state[key] = previous[key];
      }
      let step = params.get('step') || this.firstStep;
      if (!this.steps[step] || step === 'loading') step = this.firstStep;

      if (step !== 'start') {
        const steps = this.path();
        const firstMissing = () => steps.find((s) => !this.state[STEP_KEYS[s]]) || steps[steps.length - 1];
        if (step === 'ring_note' && this.state.qr !== 'nej') step = 'ring_size';
        if (step !== 'ring_note' && !steps.includes(step)) step = firstMissing();
        // Every answer before the requested step must exist
        const until = step === 'ring_note' ? steps.indexOf('ring_size') + 1 : steps.indexOf(step);
        const missing = steps.slice(0, until).find((s) => !this.state[STEP_KEYS[s]]);
        if (missing) step = missing;
      }
      if (step !== (params.get('step') || this.firstStep)) {
        history.replaceState(history.state, '', `${window.location.pathname}${window.location.search}${this.hashFor(step)}`);
      }
      this.show(step, focus && step !== this.firstStep);
    }

    show(step, focus = true) {
      const target = this.steps[step];
      if (!target) return;
      const vars = this.vars();

      Object.values(this.steps).forEach((el) => {
        el.hidden = el !== target;
        el.classList.remove('is-entering');
      });

      target.querySelectorAll('[data-gender]').forEach((group) => { group.hidden = group.dataset.gender !== (this.state.qg || 'hende'); });
      target.querySelectorAll('[data-variant]').forEach((variant) => { variant.hidden = variant.dataset.variant !== (this.state.qt === 'smykke' ? 'smykke' : 'ur'); });
      target.querySelectorAll('[data-template]').forEach((el) => { el.textContent = fill(el.dataset.template, vars); });

      // Radiogroup: reflect the saved answer, roving tabindex
      target.querySelectorAll('[role="radiogroup"]').forEach((group) => {
        const value = this.state[group.dataset.key];
        const radios = Array.from(group.querySelectorAll('[role="radio"]'));
        radios.forEach((radio) => radio.setAttribute('aria-checked', String(radio.dataset.value === value)));
        const focusable = radios.find((radio) => radio.dataset.value === value) || radios[0];
        radios.forEach((radio) => { radio.tabIndex = radio === focusable ? 0 : -1; });
      });

      if (!reducedMotion()) {
        void target.offsetWidth;
        target.classList.add('is-entering');
      }

      this.current = step;
      this.updateProgress(step);
      if (step === 'metal') this.checkAvailability(step, target);
      if (step === 'watch_type' || step === 'jewelry') this.prefetchCollections();

      if (focus) {
        const heading = Array.from(target.querySelectorAll('.gift-quiz__heading')).find((h) => h.offsetParent !== null);
        if (heading) heading.focus({ preventScroll: true });
        const top = this.getBoundingClientRect().top;
        if (top < 0) this.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
      }
    }

    updateProgress(step) {
      const inQuiz = step !== 'start';
      this.chrome.hidden = !inQuiz;
      this.querySelector('[data-quiz-back]').hidden = step === 'loading' || step === this.firstStep;
      if (!inQuiz) return;

      const steps = this.path();
      const index = step === 'ring_note' ? steps.indexOf('ring_size') : steps.indexOf(step);
      const percent = step === 'loading' ? 100 : Math.round(((index + 1) / (steps.length + 1)) * 100);
      this.progressBar.style.width = `${percent}%`;
      if (step !== 'loading' && step !== 'ring_note') {
        this.live.textContent = fill(this.live.dataset.template, Object.assign(this.vars(), { percent: percent }));
      }
    }

    vars() {
      const pronouns = (this.config.pronouns || {})[this.state.qg || 'hende'] || {};
      return { pronoun_subject: pronouns.pronoun_subject || '', pronoun_object: pronouns.pronoun_object || '' };
    }

    /* events */

    onClick(event) {
      const start = event.target.closest('[data-quiz-start]');
      if (start) {
        this.started = true;
        track('gift_quiz_start');
        this.navigate('gender');
        return;
      }
      const back = event.target.closest('[data-quiz-back]');
      if (back) {
        this.goBack();
        return;
      }
      const goto = event.target.closest('[data-quiz-goto]');
      if (goto) {
        this.navigate(goto.dataset.quizGoto);
        return;
      }
      const answer = event.target.closest('[role="radio"]');
      if (answer) this.select(answer);
    }

    onKeydown(event) {
      const radio = event.target.closest('[role="radio"]');
      if (!radio) return;
      const keys = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
      if (keys[event.key]) {
        event.preventDefault();
        const radios = Array.from(radio.closest('[role="radiogroup"]').querySelectorAll('[role="radio"]:not([hidden])'));
        const next = radios[(radios.indexOf(radio) + keys[event.key] + radios.length) % radios.length];
        radios.forEach((r) => { r.tabIndex = r === next ? 0 : -1; });
        next.focus();
      }
    }

    goBack() {
      const previous = this.previousStep(this.current);
      if (history.state && history.state.giftQuiz && history.state.depth > 0) {
        history.back();
      } else {
        this.navigate(previous, { replace: true });
      }
    }

    select(radio) {
      if (this.busy) return;
      const step = this.current;
      const key = radio.closest('[role="radiogroup"]').dataset.key;
      const value = radio.dataset.value;

      // A changed answer invalidates everything after it
      if (this.state[key] !== value) {
        ANSWER_KEYS.slice(ANSWER_KEYS.indexOf(key) + 1).forEach((k) => delete this.state[k]);
      }
      this.state[key] = value;
      radio.closest('[role="radiogroup"]').querySelectorAll('[role="radio"]').forEach((r) => r.setAttribute('aria-checked', String(r === radio)));

      // Without a start screen the quiz "starts" with the first answer
      if (!this.started) {
        this.started = true;
        track('gift_quiz_start');
      }
      track('gift_quiz_answer', { step: this.path().indexOf(step) + 1, question: TRACK_NAMES[step] || step, answer: value });

      this.busy = true;
      this.advance(step);
    }

    // Moving forward: skip the metal question when it offers at most one real choice
    async advance(step) {
      const upcoming = this.nextStep(step);
      const prefetch = upcoming === 'metal' ? withTimeout(this.availability(upcoming), 1500) : null;
      const [, counts] = await Promise.all([wait(reducedMotion() ? 0 : 220), prefetch]);

      if (upcoming === 'metal' && this.shouldSkipMetal(counts)) {
        this.metalSkipped.add(targetHandle(this.state, this.config));
      }
      const next = this.nextStep(step);

      this.busy = false;
      if (next === 'submit') {
        // Keep the last answer in the hash so "back" from the collection lands here
        history.replaceState(history.state, '', `${window.location.pathname}${window.location.search}${this.hashFor(step)}`);
        this.submit();
      } else {
        this.navigate(next);
      }
    }

    async submit() {
      this.show('loading');
      const started = Date.now();
      const target = await resolveResult(this.state, this.config);
      const url = resultUrl(target, this.state);

      this.storeResultConfig();
      track('gift_quiz_complete', {
        collection_handle: target.handle,
        filters: buildQuery(target.params),
        fallback: target.fallback || 'none'
      });

      const minimum = reducedMotion() ? 0 : 700;
      await wait(Math.max(0, minimum - (Date.now() - started)));
      window.location.href = url;
    }

    /* Hide metal answers that would give 0 products in the target collection. */
    availability(step) {
      const state = this.state;
      const config = this.config;
      const handle = targetHandle(state, config);
      const key = `${step}|${handle}`;
      if (!this.availabilityCache.has(key)) {
        const task = (async () => {
          // The filter data already carries a product count per value, so no extra
          // count requests: a metal's count is the sum over its mapped values
          const filters = await getFilters(handle);
          const counts = {};
          const mapping = config.metalMap[state.qt];
          const filter = mapping && filters.filters.find((f) => f.param_name === mapping.param);
          Object.keys((mapping && mapping.values) || {}).forEach((metal) => {
            const values = metalParams(filters, config.metalMap, state.qt, metal).map(([, value]) => value);
            counts[metal] = !filter ? 0 : filter.options
              .filter((option) => values.indexOf(option.value) !== -1)
              .reduce((sum, option) => sum + (option.count || 0), 0);
          });
          return counts;
        })().catch((error) => {
          warn('Availability check failed - showing all answers', error);
          this.availabilityCache.delete(key);
          return null;
        });
        this.availabilityCache.set(key, task);
      }
      return this.availabilityCache.get(key);
    }

    // While the type question is open, load the filter data of every collection it can lead to,
    // so the metal question doesn't wait for it after the answer
    prefetchCollections() {
      const prefix = `${this.state.qg}|${this.state.qt}|`;
      const map = this.config.collectionMap;
      Object.keys(map).filter((key) => key.indexOf(prefix) === 0).forEach((key) => {
        getFilters(map[key]).catch(() => {});
      });
    }

    // At most one real metal left: the question adds nothing, so it is skipped
    shouldSkipMetal(counts) {
      if (!counts) return false;
      return Object.keys(counts).filter((metal) => counts[metal] > 0).length <= 1;
    }

    checkAvailability(step, target) {
      const groups = Array.from(target.querySelectorAll('[role="radiogroup"]')).filter((group) => group.offsetParent !== null);
      const task = this.availability(step);
      // Only show the checking state if the counts aren't already cached
      let settled = false;
      task.then(() => { settled = true; });
      Promise.resolve().then(() => {
        if (!settled) groups.forEach((group) => group.setAttribute('aria-busy', 'true'));
      });

      // No timeout here: answers stay clickable meanwhile and update when the counts arrive
      task.then((counts) => {
        groups.forEach((group) => group.removeAttribute('aria-busy'));
        if (this.current !== step) return;

        if (step === 'metal' && this.shouldSkipMetal(counts)) {
          this.metalSkipped.add(targetHandle(this.state, this.config));
          this.submit();
          return;
        }

        groups.forEach((group) => {
          const radios = Array.from(group.querySelectorAll('[role="radio"]'));
          const empty = (radio) => Boolean(counts) && radio.dataset.value !== 'vedikke' && counts[radio.dataset.value] === 0;
          // Never hide everything: then the zero-result fallback takes over
          const hideAny = radios.some((radio) => radio.dataset.value !== 'vedikke' && !empty(radio));
          radios.forEach((radio) => {
            radio.hidden = hideAny && empty(radio);
          });
          const visible = radios.filter((radio) => !radio.hidden);
          const focusable = visible.find((radio) => radio.getAttribute('aria-checked') === 'true') || visible[0];
          radios.forEach((radio) => { radio.tabIndex = radio === focusable ? 0 : -1; });
        });

        this.updateProgress(step);
      });
    }

    // Copy for the result bar on the collection page (it can't read this section's settings)
    storeResultConfig() {
      const labels = { qs: {}, qt: {}, qm: {} };
      this.querySelectorAll('[role="radiogroup"]').forEach((group) => {
        if (!labels[group.dataset.key]) return;
        group.querySelectorAll('[role="radio"]').forEach((radio) => {
          if (radio.dataset.value !== 'vedikke') labels[group.dataset.key][radio.dataset.value] = radio.dataset.resultLabel;
        });
      });
      const data = { quizUrl: this.config.quizUrl, pronouns: this.config.pronouns, labels: labels, result: this.config.result, budgetMap: this.config.budgetMap };
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (e) {
        /* storage unavailable: the result bar uses its defaults */
      }
    }

    /* theme editor: selecting an answer block shows its question */
    previewBlock(event) {
      const radio = this.querySelector(`[data-block-id="${event.detail.blockId}"]`);
      if (!radio) return;
      const step = radio.closest('[data-step]').dataset.step;
      const gender = radio.closest('[data-gender]');
      const variant = radio.closest('[data-variant]');
      if (gender) this.state.qg = gender.dataset.gender;
      if (variant) this.state.qt = variant.dataset.variant;
      this.show(step, false);
    }

    /* debug: ?quizdebug=1 or theme editor */
    async runDebug() {
      const handles = Array.from(new Set(Object.values(this.config.collectionMap)));
      const report = { collections: {}, issues: [] };

      for (const handle of handles) {
        let filters;
        try {
          filters = await getFilters(handle);
        } catch (error) {
          report.issues.push(`${handle}: collection or ?view=quiz-filters not reachable (${error.message})`);
          continue;
        }
        report.collections[handle] = {
          count: filters.count,
          filters: filters.filters.map((f) => ({ param_name: f.param_name, values: f.values }))
        };

        const keys = Object.keys(this.config.collectionMap).filter((k) => this.config.collectionMap[k] === handle);
        const types = new Set(keys.map((k) => k.split('|')[1]));
        types.forEach((type) => {
          const mapping = this.config.metalMap[type];
          if (!mapping) return;
          const filter = filters.filters.find((f) => f.param_name === mapping.param);
          if (!filter) {
            report.issues.push(`${handle}: metal filter "${mapping.param}" is missing in Search & Discovery`);
            return;
          }
          Object.keys(mapping.values).forEach((metal) => {
            mapping.values[metal].forEach((value) => {
              if (!filter.values.includes(value)) report.issues.push(`${handle}: "${value}" (${metal}) not found in ${mapping.param}`);
            });
          });
        });
        if (!filters.filters.some((f) => f.type === 'price_range' && f.min_param === this.config.budgetMap.param_min)) {
          report.issues.push(`${handle}: price filter "${this.config.budgetMap.param_min}" is missing`);
        }
      }

      let debugScript = this.querySelector('[data-gift-quiz-debug]');
      if (!debugScript) {
        debugScript = document.createElement('script');
        debugScript.type = 'application/json';
        debugScript.dataset.giftQuizDebug = '';
        this.appendChild(debugScript);
      }
      const liquidData = parseJSON(debugScript.textContent || '{}', {}, 'debug');
      debugScript.textContent = JSON.stringify(Object.assign(liquidData, report), null, 2);

      console.groupCollapsed(`[Gavequiz] Debug: ${report.issues.length} mapping issue(s)`);
      report.issues.forEach((issue) => console.warn(issue));
      console.log(report);
      console.groupEnd();

      const panel = this.querySelector('[data-quiz-debug-panel]');
      if (panel) {
        panel.hidden = false;
        panel.innerHTML = '';
        const title = document.createElement('strong');
        title.textContent = `Gavequiz debug: ${report.issues.length} filter-værdier mangler (springes over)`;
        const list = document.createElement('ul');
        report.issues.forEach((issue) => {
          const item = document.createElement('li');
          item.textContent = issue;
          list.appendChild(item);
        });
        panel.append(title, list);
      }
    }
  }

  if (!customElements.get('gift-quiz')) customElements.define('gift-quiz', GiftQuiz);

  /* ---------- result bar (collection page, ?quiz=1) ---------- */

  class GiftQuizResultBar extends HTMLElement {
    connectedCallback() {
      this.params = new URLSearchParams(window.location.search);
      if (this.params.get('quiz') !== '1' || this.rendered) return;
      this.rendered = true;

      // Requests started early by the inline script in gift-quiz-result-bar.liquid
      const prefetch = window.giftQuizPrefetch;
      this.prefetch = prefetch;
      let stored = {};
      try {
        stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}') || {};
      } catch (e) {
        stored = {};
      }
      this.config = {
        quizUrl: stored.quizUrl || RESULT_DEFAULTS.quizUrl,
        pronouns: Object.assign({}, RESULT_DEFAULTS.pronouns, stored.pronouns),
        result: Object.assign({}, RESULT_DEFAULTS.result, stored.result),
        budgetMap: (prefetch && prefetch.budgetMap) || (stored.budgetMap && stored.budgetMap.brackets ? stored.budgetMap : RESULT_DEFAULTS.budgetMap),
        labels: {}
      };
      ['qs', 'qt', 'qm'].forEach((key) => {
        this.config.labels[key] = Object.assign({}, RESULT_DEFAULTS.labels[key], (stored.labels || {})[key]);
      });

      // Quiz params survive chip clicks even after facets.js rewrites the URL
      this.quizParams = [];
      this.params.forEach((value, name) => {
        if (name === 'quiz' || name === 'qf' || ANSWER_KEYS.includes(name)) this.quizParams.push([name, value]);
      });

      this.render();
      this.loadChips();
      track('gift_quiz_result_view', {
        collection_handle: this.dataset.handle,
        filters: buildQuery(Array.from(this.params.entries()).filter(([name]) => name.startsWith('filter.'))),
        fallback: this.params.get('qf') || 'none'
      });
    }

    render() {
      const p = this.params;
      const r = this.config.result;
      const labels = this.config.labels;
      const vars = Object.assign({}, this.config.pronouns[p.get('qg')] || this.config.pronouns.hende);
      const fallback = p.get('qf');

      const type = p.get('qs') ? labels.qs[p.get('qs')] : '';
      const metal = p.get('qm') && p.get('qm') !== 'vedikke' ? labels.qm[p.get('qm')] : '';

      // The collection banner already names the result, so there's no heading here;
      // the answers only shape the fallback notice
      const notices = { metal: r.noticeMetal, mix: r.noticeMix };
      const notice = this.querySelector('[data-result-notice]');
      if (fallback && notices[fallback]) {
        notice.textContent = fill(notices[fallback], Object.assign({ type: (type || '').toLowerCase(), metal: metal }, vars));
        notice.hidden = false;
      }

      const retake = this.querySelector('[data-result-retake]');
      retake.href = this.config.quizUrl;
      retake.textContent = r.retake;

      // "Tag quizzen igen" belongs with the collection title in the banner, centred under it.
      // Without a banner it stays in the bar.
      const title = document.querySelector('.collection-banner--content .collection-banner--title');
      if (title) {
        const holder = document.createElement('div');
        holder.className = 'gift-quiz-result__retake-holder';
        holder.appendChild(retake);
        title.insertAdjacentElement('afterend', holder);
      }
      const head = this.querySelector('.gift-quiz-result__head');
      head.hidden = notice.hidden && !head.contains(retake);

      this.hidden = false;
    }

    // The budget bracket whose price params match the current URL exactly
    activeBracket() {
      const p = new URLSearchParams(window.location.search);
      const map = this.config.budgetMap;
      const min = p.get(map.param_min || 'filter.v.price.gte');
      const max = p.get(map.param_max || 'filter.v.price.lte');
      if (!min && !max) return null;
      return Object.keys(map.brackets || {}).find((id) => {
        const b = map.brackets[id];
        const bMin = b.min !== undefined && Number(b.min) > 0 ? String(Math.round(b.min)) : null;
        const bMax = b.max !== undefined && b.max !== null ? String(Math.round(b.max)) : null;
        return (min ? String(parseFloat(min)) : null) === bMin && (max ? String(parseFloat(max)) : null) === bMax;
      }) || null;
    }

    // URL with the price set to one bracket (or removed when it's already active)
    priceUrl(id) {
      const map = this.config.budgetMap;
      const minParam = map.param_min || 'filter.v.price.gte';
      const maxParam = map.param_max || 'filter.v.price.lte';
      const current = new URLSearchParams(window.location.search);
      current.delete('page');
      this.quizParams.forEach(([name, val]) => { if (!current.has(name)) current.append(name, val); });
      const active = this.activeBracket() === id;
      const pairs = Array.from(current.entries()).filter(([n]) => n !== minParam && n !== maxParam);
      if (!active) pairs.push(...bracketParams(map, map.brackets[id]));
      return `${window.location.pathname}?${buildQuery(pairs)}`;
    }

    makeGroup(id, labelText) {
      const group = document.createElement('div');
      group.className = 'gift-quiz-result__chip-group';
      group.setAttribute('role', 'group');
      const label = document.createElement('span');
      label.className = 'gift-quiz-result__chip-label';
      label.id = `GiftQuizChips-${id.replace(/[^a-z0-9]/gi, '-')}`;
      label.textContent = labelText;
      group.setAttribute('aria-labelledby', label.id);
      const row = document.createElement('div');
      row.className = 'gift-quiz-result__chip-row';
      group.append(label, row);
      return { group, row };
    }

    makeChip(text, active, href) {
      const chip = document.createElement('a');
      chip.className = `gift-quiz-result__chip${active ? ' is-active' : ''}`;
      chip.textContent = text;
      if (active) {
        const state = document.createElement('span');
        state.className = 'visually-hidden';
        state.textContent = ` (${this.config.result.chipActive})`;
        chip.appendChild(state);
      }
      chip.href = href();
      // Recompute on click: facets.js may have changed the URL since render
      chip.addEventListener('click', () => { chip.href = href(); });
      return chip;
    }

    // "Pris" chips: only brackets with products for the current collection + filters
    // Counts per bracket for the current collection + filters; started before the filter data arrives
    priceCounts() {
      const map = this.config.budgetMap;
      const base = new URLSearchParams(window.location.search);
      [map.param_min || 'filter.v.price.gte', map.param_max || 'filter.v.price.lte', 'page', 'view'].forEach((n) => base.delete(n));
      const pairs = Array.from(base.entries()).filter(([n]) => n.startsWith('filter.'));
      const ids = Object.keys(map.brackets || {});
      return Promise.all(ids.map((id) => getCount(this.dataset.handle, pairs.concat(bracketParams(map, map.brackets[id]))).catch(() => 1)));
    }

    // All brackets right away (no network), so the price row shows with the bar
    priceGroup() {
      const map = this.config.budgetMap;
      const ids = Object.keys(map.brackets || {});
      if (ids.length < 2) return null;
      const active = this.activeBracket();
      const { group, row } = this.makeGroup('price', this.config.result.chipsPrice);
      group.classList.add('gift-quiz-result__chip-group--price');
      ids.forEach((id) => {
        const chip = this.makeChip(map.brackets[id].label, id === active, () => this.priceUrl(id));
        chip.dataset.bracket = id;
        row.appendChild(chip);
      });
      return group;
    }

    // Once the counts arrive: drop brackets without products (the active one always stays)
    prunePrice(group, counts) {
      const map = this.config.budgetMap;
      const ids = Object.keys(map.brackets || {});
      const active = this.activeBracket();
      group.querySelectorAll('[data-bracket]').forEach((chip) => {
        const index = ids.indexOf(chip.dataset.bracket);
        if (counts[index] === 0 && chip.dataset.bracket !== active) chip.remove();
      });
      // A single bracket is no choice to make
      return group.querySelectorAll('[data-bracket]').length > 1 || Boolean(active);
    }

    async loadChips() {
      const isWatch = (this.dataset.handle || '').includes('-ure-');
      const wanted = String(isWatch ? this.config.result.chipsWatch : this.config.result.chipsJewelry)
        .split(',').map((item) => item.trim()).filter(Boolean);
      const wrapper = this.querySelector('[data-result-chips-wrapper]');
      const priceRow = this.querySelector('[data-result-price]');
      const container = this.querySelector('[data-result-chips]');

      // Hold both rows' height from the start, so the products don't jump when chips arrive
      const hasBrackets = Object.keys(this.config.budgetMap.brackets || {}).length > 1;
      if (hasBrackets) {
        priceRow.hidden = false;
        priceRow.classList.add('is-loading');
      }
      if (wanted.length) {
        container.hidden = false;
        container.classList.add('is-loading');
      }
      wrapper.hidden = !hasBrackets && !wanted.length;

      const counts = this.prefetch ? this.prefetch.counts : this.priceCounts();
      // Usually already done (started early); otherwise show all brackets and prune later
      const early = await withTimeout(counts, 300);
      const priceGroup = this.priceGroup();
      priceRow.classList.remove('is-loading');
      if (priceGroup) {
        if (early) this.prunePrice(priceGroup, early);
        priceRow.appendChild(priceGroup);
      } else {
        priceRow.hidden = true;
      }

      const search = new URLSearchParams(window.location.search);
      search.delete('page');
      search.set('view', 'quiz-filters');
      let data = null;
      try {
        data = await (this.prefetch ? this.prefetch.filters : fetchJSON(`${window.location.pathname}?${search.toString()}`));
      } catch (error) {
        warn('Could not load quickfilters', error);
      }

      const matches = (param, name) => param === name || param.endsWith(`.${name}`);
      if (data) {
        wanted.forEach((name) => {
          const filter = data.filters.find((f) => f.type === 'list' && matches(f.param_name, name));
          if (!filter) return; // Not on this collection: render nothing
          const options = filter.options.filter((o) => o.active || o.count > 0);
          if (!options.length) return;

          const { group, row } = this.makeGroup(filter.param_name, filter.label);
          options.forEach((option) => {
            row.appendChild(this.makeChip(option.label, option.active, () => this.chipUrl(filter.param_name, option.value)));
          });
          container.appendChild(group);
        });
      }
      container.classList.remove('is-loading');
      container.hidden = !container.children.length;

      if (priceGroup) {
        const map = this.config.budgetMap;
        const hasPriceFilter = !data || data.filters.some((f) => f.type === 'price_range' && f.min_param === (map.param_min || 'filter.v.price.gte'));
        const keep = hasPriceFilter && this.prunePrice(priceGroup, await counts);
        if (!keep) {
          priceGroup.remove();
          priceRow.hidden = true;
        }
      }
      wrapper.hidden = priceRow.hidden && container.hidden;
    }

    // Toggle one filter value, keeping every other param (existing filters + quiz params)
    chipUrl(param, value) {
      const current = new URLSearchParams(window.location.search);
      current.delete('page');
      this.quizParams.forEach(([name, val]) => { if (!current.has(name)) current.append(name, val); });
      const pairs = Array.from(current.entries());
      const active = pairs.some(([n, v]) => n === param && v === value);
      const next = active ? pairs.filter(([n, v]) => !(n === param && v === value)) : pairs.concat([[param, value]]);
      return `${window.location.pathname}?${buildQuery(next)}`;
    }
  }

  if (!customElements.get('gift-quiz-result-bar')) customElements.define('gift-quiz-result-bar', GiftQuizResultBar);
})();
